import { useState } from 'react';

import { Link } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, MonitorCog, Settings } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useHasTeam } from '@/hooks/useHasTeam';
import { usePermissionRenderer } from '@/hooks/usePermissions';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { UserAvatar } from '@/components/user-avatar';

import { User } from './types';

interface UserProfileMenuProps {
    user: User | null | undefined;
    onLogout?: () => void;
}

const UserProfileMenu = ({ user, onLogout }: UserProfileMenuProps) => {
    const { when, unless } = usePermissionRenderer();
    const hasTeam = useHasTeam();

    const [isOpen, setIsOpen] = useState(false);

    const handleLogout = () => {
        setIsOpen(false);
        onLogout?.();
    };

    return (
        <div className="flex justify-end items-center">
            <div className="flex items-center w-full space-x-4">
                <div className="relative w-full">
                    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                        <DropdownMenuTrigger
                            data-testid="UserProfileMenuTrigger"
                            asChild
                        >
                            <SidebarMenuButton
                                size="lg"
                                className={cn(
                                    'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
                                    'justify-between',
                                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                )}
                            >
                                <div className="flex items-center gap-3">
                                    <UserAvatar
                                        name={user?.name || 'Guest'}
                                        size="sm"
                                    />
                                    <div className="flex flex-col items-start">
                                        <span className="font-medium text-sm">
                                            {user?.name || 'Guest'}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {user?.username || 'guest'}
                                        </span>
                                    </div>
                                </div>
                                <ChevronsUpDown className="ml-auto size-4" />
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="p-2 w-56">
                            <div className="flex items-center gap-2 p-2 mb-2">
                                <UserAvatar
                                    name={user?.name || 'Guest'}
                                    size="sm"
                                />
                                <div className="flex flex-col">
                                    <span className="font-medium text-sm">
                                        {user?.name || 'Guest'}
                                    </span>
                                </div>
                            </div>

                            <DropdownMenuSeparator />

                            {hasTeam && (
                                <>
                                    <DropdownMenuItem asChild className="gap-2">
                                        <Link
                                            to="/settings"
                                            className="flex items-center w-full"
                                        >
                                            <Settings className="h-4 w-4 text-gray-500" />
                                            Settings
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild className="gap-2">
                                        <Link
                                            to="/system"
                                            className="flex items-center w-full"
                                            data-testid="MenuItem__system"
                                        >
                                            <MonitorCog className="h-4 w-4 text-gray-500" />
                                            System
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                </>
                            )}

                            <DropdownMenuItem
                                className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50"
                                data-testid="MenuItem__logout"
                                onClick={handleLogout}
                            >
                                <LogOut className="h-4 w-4" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
    );
};

export default UserProfileMenu;
